export const CHARACTER_BULLET = '•';

const NON_WORDS_REGEX = /\s+/g;

export const splitToWords = (text: string = ''): string[] =>
  text.split(NON_WORDS_REGEX).filter(Boolean);

export const countWords = (text?: string): number => splitToWords(text || '').length;

export const countSentences = (text?: string): number =>
  text?.split(/[.!?]+\s/).filter(Boolean).length || 0;

export const countParagraphs = (text?: string): number => {
  if (text === '') {
    return 0;
  }

  return text?.split(/[\r\n]{2,}(?!\s*$)/).length || 0;
};

export const countTextSyllables = (text?: string): number => {
  if (!text) {
    return 0;
  }

  return text
    .trim()
    .split(/\W+/) // splitToWords count dots and delimiters as part of the word.
    .reduce((count, word) => count + countWordSyllables(word), 0);
};

const countWordSyllables = (word: string): number => {
  if (!word) {
    return 0;
  }

  word = word.toLowerCase().trim(); //word.downcase!

  const hasNonEnglishLetters = /[^a-zA-Z]/.test(word);
  if (hasNonEnglishLetters) {
    return 0;
  }

  if (word.length <= 3 && word.length > 1) {
    return 1;
  }

  word = word.replace(/(?:[^laeiouyc]es|ed|[^laeiouy]e)$/, '');
  word = word.replace(/^y/, '');

  return word.match(/[aeiouy]{1,2}/g)?.length || 0;
};

export const countChars = (text: string): number => (text || '').trim().length;

export const haveTrimmedEnd = (text: string = ''): boolean => {
  if (text.length === 0) {
    return false;
  }

  return !text[text.length - 1].match(NON_WORDS_REGEX);
};

export const appendText = (source?: string, addition?: string, separator?: string): string => {
  const preparedSource = source || '';
  const preparedAddition = addition || '';
  const preparedSeparator = separator || '';

  const lastSourceChar = preparedSource[preparedSource.length - 1];
  const firstAdditionChar = preparedAddition[0];

  const needsSeparator = separator
    ? lastSourceChar &&
      firstAdditionChar &&
      ![lastSourceChar, firstAdditionChar].includes(separator)
    : false;

  if (!preparedSeparator || !needsSeparator) {
    return `${preparedSource}${preparedAddition}`;
  }

  return `${preparedSource}${separator}${preparedAddition}`;
};

export const replaceBetween = (str: string, start: number, end: number, what: string): string => {
  return str.slice(0, start) + what + str.slice(end + 1);
};

export const toPascalCase = (text: string): string => {
  return text.replace(/(\w)(\w*)/g, (_g0, g1, g2) => g1.toUpperCase() + g2.toLowerCase());
};

export const areEmptyOrEqualStrings = (str1: string, str2: string): boolean =>
  str1 === str2 || (!str1 && !str2);

/**
 * Get a hash of input string
 * Source: https://stackoverflow.com/a/7616484
 */
export const hashString = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    const chr = str.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0; // Convert to 32bit integer
  }
  return hash;
};

/**
 * Split a string by multiple tokens
 * @param str - String to split
 * @param tokens - Array of strings
 * @returns Array of string separated by the tokens provided
 */
export const splitStrByMultipleTokens = (str: string, tokens: string[]): string[] => {
  let strToSplit = str;
  const tempChar = tokens[0];
  for (let i = 1; i < tokens.length; i += 1) {
    strToSplit = strToSplit.split(tokens[i]).join(tempChar);
  }
  return strToSplit.split(tempChar).filter(Boolean);
};

export const getWordBoundaryRegExp = (str: string): RegExp =>
  new RegExp(`(^|\\W)${str}(?!\\w)`, 'gi');

/**
 * Get a string with indefinite article for provided text
 * @param text - text to get article for
 * @returns indefinite article
 */
export const determineIndefiniteArticle = (text?: string): string => {
  if (!text) {
    return '';
  }

  const firstLetter = text[0] || '';
  if (['a', 'e', 'i', 'o', 'u'].includes(firstLetter.toLowerCase())) {
    return 'an';
  }

  return 'a';
};

export const isSentenceEndCharacter = (char: string): boolean => {
  return /[.!?。]/.test(char);
};

export const isSpaceCharacter = (char: string): boolean => {
  return /\s/.test(char);
};

export const isAlphaNumeric = (text: string): boolean => {
  return /^[a-z0-9]*$/i.test(text);
};

export const omitNonAlphaNumericCharacters = (text?: string): string => {
  if (!text) {
    return '';
  }

  return text.replace(/[^a-z0-9]/gi, '');
};

/**
 * Check if text contains hebrew characters
 * @param text - text to check
 * @returns true if text contains hebrew characters
 */
export function containsHebrew(text?: string): boolean {
  if (!text) {
    return false;
  }

  return /[\u0590-\u05FF]/.test(text);
}

/**
 * Check if text contains newline characters only
 * @param str - text to check
 * @returns true if text contains only newline characters
 */
export const containsOnlyNewlines = (str: string): boolean => {
  if (!str) {
    return false;
  }
  return /^(\r?\n)*$/.test(str);
};

/**
 * Count the number of newline characters in a string
 * @param str - text to count newlines in
 * @returns number of newline characters
 */
export const countNewlines = (str: string): number => (str.match(/\r?\n/g) || []).length;

/**
 * Replaces `{key}` tokens in a template with the matching value from `values`.
 * Unknown tokens are left untouched, so a template can reference placeholders
 * a given call doesn't supply without being mangled.
 *
 * Extend by adding keys to the `values` record passed by the caller — no change
 * here is needed for new placeholders.
 */
export const interpolatePlaceholders = (
  template: string,
  values: Readonly<Record<string, string>>
): string => template.replace(/{(\w+)}/g, (token, key) => (key in values ? values[key] : token));
